/**
 * Feature Flag altyapısı — Operasyon Keskin Göz Faz 9.4 (2026-05-19).
 *
 * Riskli UI veya backend bağımlı yeni özellikleri `.env` üzerinden açıp kapatmayı sağlar.
 * Deploy sırasında canlıya patlama olmadan dark-launch yapılabilir.
 *
 * `.env` örnek:
 *   REACT_APP_FF_NEW_DASHBOARD=false
 *   REACT_APP_FF_PICKER_REDESIGN=true
 *   REACT_APP_FF_BUNDLE_PARENT_BADGE=false
 *
 * Kullanım — Component içinde:
 *   import { useFeatureFlag } from 'contexts/FeatureFlagContext';
 *   const newDashboardEnabled = useFeatureFlag('NEW_DASHBOARD');
 *   if (newDashboardEnabled) return <NewDashboard />;
 *   return <OldDashboard />;
 *
 * Kullanım — JSX inline:
 *   import { FeatureFlag } from 'contexts/FeatureFlagContext';
 *   <FeatureFlag name="NEW_DASHBOARD"><NewDashboard /></FeatureFlag>
 *
 * Override (geliştirme/test için):
 *   <FeatureFlagProvider overrides={{ NEW_DASHBOARD: true }}>...</FeatureFlagProvider>
 *
 * Tasarım:
 *  - `.env` build-time → REACT_APP_FF_<NAME> okunur ve 'true' string'ine eşit ise aktif
 *  - Runtime override (örn. admin paneli) için Provider'a `overrides` prop'u geçilir
 *  - Listede olmayan / okunamayan flag'ler default `false` → güvenli kapalı
 */
import React, { createContext, useContext, useMemo } from 'react';

const FeatureFlagContext = createContext({ flags: {}, isEnabled: () => false });

/**
 * .env'den REACT_APP_FF_* prefix'ini taşıyan değişkenleri tara, boolean'a çevir.
 * Sadece 'true' (case-insensitive) string'i aktif sayılır; her başka değer false.
 */
function readEnvFlags() {
  const out = {};
  if (typeof process === 'undefined' || !process.env) return out;
  for (const [key, value] of Object.entries(process.env)) {
    if (!key.startsWith('REACT_APP_FF_')) continue;
    const name = key.slice('REACT_APP_FF_'.length);
    out[name] = String(value).toLowerCase() === 'true';
  }
  return out;
}

export function FeatureFlagProvider({ children, overrides }) {
  const value = useMemo(() => {
    const envFlags = readEnvFlags();
    const merged = { ...envFlags, ...(overrides || {}) };
    return {
      flags: merged,
      isEnabled: (name) => Boolean(merged[name]),
    };
  }, [overrides]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/** Tek bir flag adına göre boolean dönen kanca. */
export function useFeatureFlag(name) {
  const ctx = useContext(FeatureFlagContext);
  return ctx.isEnabled(name);
}

/** Tüm flag map'ini almak isteyen (debug paneli vb.) için. */
export function useAllFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  return ctx.flags;
}

/** Bildirimsel JSX kullanım: flag açıksa children render edilir. */
export function FeatureFlag({ name, fallback = null, children }) {
  const enabled = useFeatureFlag(name);
  return enabled ? <>{children}</> : <>{fallback}</>;
}

export default FeatureFlagContext;
